import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useLazyGetEsignDocumentByIdQuery,
  useOpenEsignSenderViewMutation,
} from '@/store/query/esign-documents';
import type { EsignDocumentEnvelope } from '@/types/esignature';

// ~40s, matching the offer-side preview budget. Running out is recoverable: the
// next click enqueues a fresh attempt rather than resuming this one.
const POLL_MAX_RETRIES = 20;
const POLL_INTERVAL_MS = 2000;

/** Thrown when the backend gave no reason worth showing the recruiter. */
export const ESIGN_PREVIEW_GENERIC_ERROR = 'esign_preview_error';

/** Generic title, backend reason as the description — the shape the toasts use. */
export const esignPreviewErrorToast = (error: unknown, title: string) => {
  const reason = (error as Error)?.message;
  return {
    title,
    description:
      reason && reason !== ESIGN_PREVIEW_GENERIC_ERROR ? reason : undefined,
    status: 'error' as const,
  };
};

const formatEnvelopeError = (envelope?: EsignDocumentEnvelope) =>
  [envelope?.error?.code, envelope?.error?.message]
    .filter(Boolean)
    .join(': ')
    .toLowerCase() || undefined;

type PollOutcome =
  | { kind: 'ready' }
  | { kind: 'failed'; reason?: string }
  | { kind: 'timeout' }
  | { kind: 'cancelled' };

type PollEnvelope = ReturnType<typeof useLazyGetEsignDocumentByIdQuery>[0];

/**
 * Polls until the DocuSign draft exists.
 *
 * Two terminal conditions, per the send API: `external_id_present` flips true
 * once the draft reaches DocuSign, and a non-null `error` means the send failed
 * before it got there — in which case `external_id_present` never becomes true,
 * so stopping early is the difference between a message and a 40-second hang.
 * A poll request that itself fails is a failed request, not a failed preview; it
 * only spends a retry.
 */
export const pollUntilEsignDraftReady = async (
  pollEnvelope: PollEnvelope,
  id: number | string,
  isCancelled: () => boolean,
  maxRetries = POLL_MAX_RETRIES,
  interval = POLL_INTERVAL_MS
): Promise<PollOutcome> => {
  let retries = 0;
  while (retries < maxRetries) {
    if (isCancelled()) return { kind: 'cancelled' };
    const response = await pollEnvelope({ id });
    const envelope = response?.data as EsignDocumentEnvelope | undefined;
    if (envelope) {
      if (envelope.error) {
        return { kind: 'failed', reason: formatEnvelopeError(envelope) };
      }
      if (envelope.external_id_present) return { kind: 'ready' };
    }
    retries += 1;
    await new Promise((resolve) => setTimeout(resolve, interval));
  }
  return { kind: 'timeout' };
};

interface RunPreviewArgs {
  /** Envelope id from the 202 returned by preview or preview_adhoc. */
  id: number | string;
}

/**
 * The three-call preview: enqueue (done by the caller), poll, redirect.
 *
 * The redirect is same-tab so no popup blocker sees it and no user-activation
 * window is needed, which is what allows the draft to be built asynchronously.
 */
export const useEsignPreviewFlow = () => {
  const [pollEnvelope] = useLazyGetEsignDocumentByIdQuery();
  const [openSenderView] = useOpenEsignSenderViewMutation();
  const [isRunning, setIsRunning] = useState(false);

  // The success path navigates the tab, so a drawer closed mid-poll must neither
  // redirect nor toast. Reset on setup as well as cleanup, or a StrictMode
  // double-invoke leaves the ref true on a mounted hook and cancels every run.
  const isUnmountedRef = useRef(false);
  useEffect(() => {
    isUnmountedRef.current = false;
    return () => {
      isUnmountedRef.current = true;
    };
  }, []);

  const runPreview = useCallback(
    async ({ id }: RunPreviewArgs) => {
      setIsRunning(true);
      try {
        const outcome = await pollUntilEsignDraftReady(
          pollEnvelope,
          id,
          () => isUnmountedRef.current
        );
        if (isUnmountedRef.current) return;
        if (outcome.kind === 'failed') {
          throw new Error(outcome.reason || ESIGN_PREVIEW_GENERIC_ERROR);
        }
        if (outcome.kind === 'timeout') {
          throw new Error(ESIGN_PREVIEW_GENERIC_ERROR);
        }

        const response = await openSenderView({ id });
        if (isUnmountedRef.current) return;
        const url = 'data' in response ? response.data?.url : undefined;
        if ('error' in response || !url) {
          throw new Error(ESIGN_PREVIEW_GENERIC_ERROR);
        }
        window.location.href = url;
      } catch (error) {
        setIsRunning(false);
        throw error;
      }
    },
    [pollEnvelope, openSenderView]
  );

  return { runPreview, isRunning };
};
